import { useState } from 'react';
import { Button, Card } from '@lms/ui';
import { X, Share2 } from 'lucide-react';
import { useCreateFlow, useUpdateFlow } from '../hooks/useLearningFlows';
import type { LearningFlow } from '@lms/shared/types';

interface FlowModalProps {
  programId: string;
  levelId: string;
  order: number;
  flow?: LearningFlow;
  onClose: () => void;
}

export function FlowModal({ programId, levelId, order, flow, onClose }: FlowModalProps) {
  const [name, setName] = useState(flow?.name || '');
  const [description, setDescription] = useState(flow?.description || '');
  const [isSequential, setIsSequential] = useState(flow?.isSequential || false);
  const createFlowMutation = useCreateFlow();
  const updateFlowMutation = useUpdateFlow();

  const isEditing = !!flow;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;

    if (isEditing) {
      updateFlowMutation.mutate(
        {
          id: flow.id,
          data: {
            name,
            description,
            order,
            isSequential,
            programId,
          },
        },
        {
          onSuccess: () => onClose(),
        }
      );
    } else {
      createFlowMutation.mutate(
        {
          programId,
          levelId,
          name,
          description,
          order,
          isSequential,
        },
        {
          onSuccess: () => onClose(),
        }
      );
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-center p-4 bg-on-surface/20 backdrop-blur-sm animate-fadeIn overflow-y-auto py-12">
      <Card className="w-full max-w-md !p-0 overflow-hidden shadow-level5 border border-on-surface/5 animate-scaleIn my-auto">
        <div className="p-6 border-b border-outline-variant/10 flex justify-between items-center bg-surface-container-low">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center">
              <Share2 size={20} />
            </div>
            <div>
              <h3 className="text-lg font-headline font-bold text-on-surface m-0">
                {isEditing ? 'Flow Refinement' : 'Initialize Flow'}
              </h3>
              <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60">
                {isEditing ? 'Optimizing Sub-Phase Module' : 'Sub-Phase Module Configuration'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-container-high rounded-full transition-colors text-on-surface-variant"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          <div className="space-y-6">
            <div className="space-y-2">
              <label
                htmlFor="flow-name"
                className="text-[10px] font-label font-black text-secondary uppercase tracking-[0.2em] ml-1"
              >
                Flow Identity
              </label>
              <input
                id="flow-name"
                autoFocus
                className="w-full bg-surface-container-low border-none rounded-xl px-5 py-4 text-base font-body focus:ring-2 focus:ring-secondary/20 transition-all outline-none"
                placeholder="e.g., Structural Integrity Analysis"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="flow-desc"
                className="text-[10px] font-label font-black text-on-surface-variant uppercase tracking-[0.2em] ml-1"
              >
                Instructional Scope
              </label>
              <textarea
                id="flow-desc"
                className="w-full bg-surface-container-low border-none rounded-xl px-5 py-4 text-sm font-body focus:ring-2 focus:ring-secondary/20 transition-all outline-none min-h-[100px] resize-none"
                placeholder="Deep dive into materials and methodologies..."
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between bg-surface-container-low/50 p-5 rounded-2xl border border-on-surface/5">
              <div className="space-y-0.5">
                <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60">
                  Access Model
                </p>
                <p className="text-xs font-bold text-on-surface">Enforce Sequential Flow</p>
              </div>
              <button
                type="button"
                onClick={() => setIsSequential(!isSequential)}
                className={`w-12 h-6 rounded-full transition-all relative ${isSequential ? 'bg-secondary' : 'bg-on-surface-variant/20'}`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${isSequential ? 'left-7' : 'left-1'}`}
                />
              </button>
            </div>
          </div>

          <div className="pt-4 flex gap-4">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="flex-1 h-12 rounded-xl text-[10px] font-black uppercase tracking-widest"
            >
              Abort
            </Button>
            <Button
              type="submit"
              disabled={createFlowMutation.isPending || !name}
              className="flex-[2] h-12 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-level2 bg-secondary text-on-secondary hover:bg-secondary-container transition-colors"
            >
              {createFlowMutation.isPending || updateFlowMutation.isPending
                ? 'Synchronizing...'
                : isEditing
                  ? 'Commit Refinement'
                  : 'Deploy Flow'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
